import {
  createRoute,
  Outlet,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { getBrandFont } from '@erp/json-renderer';
import { Route as rootRoute } from 'src/pages/__root';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { PortalBrandTheme } from './brandTheme';
import { cannedBrandTokens, cannedCompanyName } from './cannedPortal';
import { clearPortalSession, isPortalLoggedIn } from './portal-session';

const headingFamily =
  getBrandFont(cannedBrandTokens, 'heading')?.family ?? 'Georgia, serif';

/**
 * The brand wordmark shown in the portal header and on the login card. A small
 * "columns" glyph nods to the company name without depending on a hosted logo.
 */
export function PortalWordmark({ className }: { className?: string }) {
  return (
    <span className={className} style={{ fontFamily: headingFamily }}>
      <svg
        width="18"
        height="20"
        viewBox="0 0 18 20"
        fill="none"
        aria-hidden="true"
        className="inline-block mr-2 align-[-3px]"
      >
        <rect x="0" y="2" width="18" height="2.5" rx="1" fill="currentColor" />
        <rect x="2" y="6" width="2.5" height="9" fill="currentColor" />
        <rect x="7.75" y="6" width="2.5" height="9" fill="currentColor" />
        <rect x="13.5" y="6" width="2.5" height="9" fill="currentColor" />
        <rect x="0" y="16" width="18" height="2.5" rx="1" fill="currentColor" />
      </svg>
      {cannedCompanyName}
    </span>
  );
}

/**
 * Pathless layout route for the demo portal. Gates everything under it on the
 * local portal session and renders the standalone branded chrome (no Gently
 * sidebar/topbar). Mirrors the main app's `pathlessAuthenticatedRoute` pattern.
 */
export const portalAuthenticatedRoute = createRoute({
  id: 'portal',
  getParentRoute: () => rootRoute,
  beforeLoad: () => {
    if (!isPortalLoggedIn()) {
      throw redirect({ to: '/portal/login' });
    }
  },
  component: PortalShell,
});

function PortalShell() {
  const navigate = useNavigate();

  function handleLogout() {
    clearPortalSession();
    void navigate({ to: '/portal/login' });
  }

  return (
    <PortalBrandTheme
      tokens={cannedBrandTokens}
      className="min-h-svh flex flex-col"
    >
      <header
        className="flex h-16 shrink-0 items-center justify-between px-6"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        <PortalWordmark className="text-xl font-semibold tracking-tight" />
        <Button variant="outline" size="sm" onClick={handleLogout}>
          Log out
        </Button>
      </header>
      <main className="flex-1 min-h-0 w-full overflow-y-auto">
        <div className="mx-auto w-full max-w-5xl">
          <Outlet />
        </div>
      </main>
      {/* Portal is outside the authenticated layout (which owns the app's
          Toaster), so mount one here for the dashboard's action toasts. */}
      <Toaster position="bottom-right" />
    </PortalBrandTheme>
  );
}
